import { Box, Typography } from "@mui/material";
import { isEmpty } from "lodash";
import React from "react";

export const getStatusColor = (status?: string) => {
  switch (status) {
    case "red_alert":
      return { color: "#EF3826", backgroundColor: "#FFF2F0" };
    case "orange_alert":
      return { color: "#F47500", backgroundColor: "#FFEEDF" };
    default:
      return { color: null, backgroundColor: null };
  }
};

export const renderDetailTooltip = (detail) => {
  const conditionTriggered = detail?.["Condition Triggered"];
  const otherEntries = Object.entries(detail || {}).filter(
    ([key]) => key !== "Condition Triggered"
  );

  return (
    <Box>
      {conditionTriggered && (
        <Box sx={{ display: "flex", gap: 5, justifyContent: "space-between" }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "10px" }}>
            Condition Triggered:
          </Typography>
          <Typography sx={{ fontSize: "10px" }}>
            {conditionTriggered}
          </Typography>
        </Box>
      )}

      {otherEntries.map(([key, value]) => (
        <Box
          key={key}
          sx={{ display: "flex", gap: 5, justifyContent: "space-between" }}
        >
          <Typography sx={{ fontWeight: "bold", fontSize: "10px" }}>
            {`${key}:`}
          </Typography>
          <Typography sx={{ fontSize: "10px" }}>{value}</Typography>
        </Box>
      ))}
    </Box>
  );
};

export const getMetrics = (data) => {
  const getValidDate = (timestamp) => {
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const selectLatestMeasurement = (data) => {
    const bpDate = getValidDate(data?.bloodpressure?.timestamp);
    const poDate = getValidDate(data?.pulseox?.timestamp);

    if (bpDate && poDate) {
      return bpDate > poDate ? data.bloodpressure : data.pulseox;
    }
    return data?.bloodpressure || data?.pulseox || null;
  };

  const lastMeasures = selectLatestMeasurement(data);
  const bpm = lastMeasures?.value?.bpm;

  return [
    {
      key: "systolic",
      label: "Systolic BP",
      value: data?.bloodpressure?.value?.systolic,
      unit: "mg/dl",
      measureType: "bloodpressure",
      status: data?.bloodpressure?.measure_alert?.content?.systolic?.status,
    },
    {
      key: "diastolic",
      label: "Diastolic BP",
      value: data?.bloodpressure?.value?.diastolic,
      unit: "mmHg",
      measureType: "bloodpressure",
      status: data?.bloodpressure?.measure_alert?.content?.diastolic?.status,
    },
    {
      key: "bpm",
      label: "Heart Rate",
      value: bpm,
      unit: "bpm",
      measureType: "bloodpressure",
      status: lastMeasures?.measure_alert?.content?.bpm?.status,
    },
    {
      key: "weight",
      label: "Weight",
      value: data?.weight?.value?.weight_lbs,
      unit: "lbs",
      measureType: "weight",
      status: data?.weight?.measure_alert?.content?.weight_lbs?.status,
    },
    {
      key: "spo2",
      label: "Pulse Oximeter",
      value: data?.pulseox?.value?.spo2,
      unit: "%",
      measureType: "pulseox",
      status: data?.pulseox?.measure_alert?.content?.spo2?.status,
    },
  ];
};

export const getMetricsHtn = (data) => [
  {
    key: "systolic",
    label: "Systolic BP",
    value: data?.bloodpressure?.value?.systolic,
    unit: "mmHg",
    measureType: "bloodpressure",
    status: data?.bloodpressure?.measure_alert?.content?.systolic?.status,
  },
  {
    key: "diastolic",
    label: "Diastolic BP",
    value: data?.bloodpressure?.value?.diastolic,
    unit: "mmHg",
    measureType: "bloodpressure",
    status: data?.bloodpressure?.measure_alert?.content?.diastolic?.status,
  },
  {
    key: "bpm",
    label: "Heart Rate",
    value: data?.bloodpressure?.value?.bpm,
    unit: "bpm",
    measureType: "bloodpressure",
    status: data?.bloodpressure?.measure_alert?.content?.bpm?.status,
  },
];

export const getMetricsCopd = (data) => [
  {
    key: "spo2",
    label: "Pulse Oximeter",
    value: data?.pulseox?.value?.spo2,
    unit: "%",
    measureType: "pulseox",
    status: data?.pulseox?.measure_alert?.content?.spo2?.status,
  },
  {
    key: "bpm",
    label: "Heart Rate",
    value: data?.pulseox?.value?.bpm,
    unit: "BPM",
    measureType: "pulseox",
    status: data?.pulseox?.measure_alert?.content?.bpm?.status,
  },
  {
    key: "celsius",
    label: "Temperature",
    value: data?.temperature?.value?.celsius,
    unit: "celsius",
    measureType: "temperature",
    status: data?.temperature?.measure_alert?.content?.celsius?.status,
  },
];

export const getMetricsCkd = (data) => {
  const getValidDate = (timestamp) => {
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const selectLatestMeasurement = (data) => {
    const bpDate = getValidDate(data?.bloodpressure?.timestamp);
    const poDate = getValidDate(data?.pulseox?.timestamp);

    if (bpDate && poDate) {
      return bpDate > poDate ? data.bloodpressure : data.pulseox;
    }
    return data?.bloodpressure || data?.pulseox || null;
  };

  const lastMeasures = selectLatestMeasurement(data);
  const bpm = lastMeasures?.value?.bpm;
  return [
    {
      key: "spo2",
      label: "Pulse Oximeter",
      value: data?.pulseox?.value?.spo2,
      unit: "%",
      measureType: "pulseox",
      status: data?.pulseox?.measure_alert?.content?.spo2?.status,
    },
    {
      key: "bpm",
      label: "Heart Rate",
      value: bpm,
      unit: "BPM",
      measureType: "pulseox",
      status: lastMeasures?.measure_alert?.content?.bpm?.status,
    },
    {
      key: "celsius",
      label: "Temperature",
      value: data?.temperature?.value?.celsius,
      unit: "celsius",
      measureType: "temperature",
      status: data?.temperature?.measure_alert?.content?.celsius?.status,
    },
    {
      key: "systolic",
      label: "Systolic BP",
      value: data?.bloodpressure?.value?.systolic,
      unit: "mg/dl",
      measureType: "bloodpressure",
      status: data?.bloodpressure?.measure_alert?.content?.systolic?.status,
    },
    {
      key: "diastolic",
      label: "Diastolic BP",
      value: data?.bloodpressure?.value?.diastolic,
      unit: "mmHg",
      measureType: "bloodpressure",
      status: data?.bloodpressure?.measure_alert?.content?.diastolic?.status,
    },
    {
      key: "weight",
      label: "Weight",
      value: data?.weight?.value?.weight_lbs,
      unit: "lbs",
      measureType: "weight",
      status: data?.weight?.measure_alert?.content?.weight_lbs?.status,
    },
  ];
};

export const getLastMeasureResults = (lastMeasures) => {
  if (isEmpty(lastMeasures)) return [];

  return Object.entries(lastMeasures).map(([type, data]) => ({
    type,
    value: data?.value || {},
    measure_alert: data?.measure_alert?.content || {},
    timestamp: data?.timestamp,
  }));
};

export const priority = {
  red_alert: 3,
  orange_alert: 2,
  normal: 1,
};

export const getMostSevereStatus = (input) => {
  if (isEmpty(input)) return null;
  return Object.entries(input).reduce(
    (mostSevere, [key, value]) => {
      const currentSeverity = priority[value?.status] || 0;
      const mostSevereSeverity = priority[mostSevere?.value?.status] || 0;

      return currentSeverity > mostSevereSeverity ? { key, value } : mostSevere;
    },
    { key: null, value: null }
  );
};

export const ALERT_RULES_HTN = {
  title: "Hypertension (HTN)",
  measure: [
    {
      type: "Systolic BP",
      yellowAlert: [
        ">/=140; <220",
        "Trending < or > 30 mm Hg from patient's baseline (average BP over last 7 days)",
      ],
      redAlert: [
        ">/= 220",
        "Trending < or > 40 mm Hg from patient's baseline (average BP over last 7 days)",
      ],
    },
    {
      type: "Diastolic BP",
      yellowAlert: ["90-109"],
      redAlert: [">/= 110"],
    },
    {
      type: "Heart Rate",
      yellowAlert: [
        "Abrupt change in heart rate trend, defined as an increase or decrease of more than 30 BPM in 48 hours",
      ],
      redAlert: [">140 or </= 40"],
    },
  ],
};

export const ALERT_RULES_CHF = {
  title: "Congestive Heart Failure (CHF)",
  measure: [
    {
      type: "Systolic BP",
      yellowAlert: [
        ">/=141; <180",
        "Trending < or > 30 mm Hg from patient's baseline (average BP over last 7 days)",
        "Trending >140, greater than 30% of the time over the last 14-day period",
      ],
      redAlert: [">/= 180"],
    },
    {
      type: "Diastolic BP",
      yellowAlert: ["90 - 99"],
      redAlert: [">/= 100"],
    },
    {
      type: "Heart Rate",
      yellowAlert: [
        "Abrupt change in heart rate trend, defined as an increase or decrease of more than 30 BPM in 48 hours",
      ],
      redAlert: [">120 or </= 45"],
    },
    {
      type: "Weight",
      yellowAlert: ["Gain of 3-5 lbs. in 1 day", "Gain of 5 lbs. in 5 days"],
      redAlert: ["Weight gain of >/= 5 lbs. in 1 day"],
    },
    {
      type: "Oxygen Saturation",
      yellowAlert: ["81-89%", "Trending saturation has 4% drop in 24 hours"],
      redAlert: ["</= 81%"],
    },
  ],
};

export const ALERT_RULES_COPD = {
  title: "Chronic Obstructive Pulmonary Disease (COPD)",
  measure: [
    {
      type: "Oxygen Saturation",
      yellowAlert: ["81-89%", "Trending saturation has 4% drop in 24hours"],
      redAlert: ["</=81%"],
    },
    {
      type: "Heart Rate",
      yellowAlert: [
        "HR of 45-59 or 101-119",
        "Abrupt change in HR trend, defined as an increase/decrease of more than 30 BPM in 48 hours",
      ],
      redAlert: [
        "HR </=45 or >/=120",
        "Abrupt change in HR trend, defined as an increase/decrease of more than 50 BPM in 48 hours",
      ],
    },
    {
      type: "Temperature",
      yellowAlert: ["Between 96.1-96.9", "Between 99.6-101.4"],
      redAlert: [
        "</=96 or >/=101.5",
        "3 days outside of 97-99.5 temperature range",
      ],
    },
  ],
};

export const ALERT_RULES_CKD = {
  title: "Chronic Kidney Disease (CKD)",
  measure: [
    {
      type: "Oxygen Saturation",
      yellowAlert: ["81-89%", "Trending saturation has 4% drop in 24hours"],
      redAlert: ["</=81%"],
    },
    {
      type: "Heart Rate",
      yellowAlert: [
        "HR of 45-59 or 101-119",
        "Abrupt change in HR trend, defined as an increase/decrease of more than 30 BPM in 48 hours",
      ],
      redAlert: [
        "HR </=45 or >/=120",
        "Abrupt change in HR trend, defined as an increase/decrease of more than 50 BPM in 48 hours",
      ],
    },
    {
      type: "Temperature",
      yellowAlert: ["Between 96.1-96.9", "Between 99.6-101.4"],
      redAlert: [
        "</=96 or >/=101.5",
        "3 days outside of 97-99.5 temperature range",
      ],
    },
    {
      type: "Systolic BP",
      yellowAlert: [
        ">/=141; <180",
        "Trending < or > 30 mm Hg from patient's baseline (average BP over last 7 days)",
        "Trending >140, greater than 30% of the time over the last 14-day period",
      ],
      redAlert: [">/= 180"],
    },
    {
      type: "Diastolic BP",
      yellowAlert: ["90 - 99"],
      redAlert: [">/= 100"],
    },
    {
      type: "Weight",
      yellowAlert: [
        "Weight change of 3-5 lbs. in 1 day",
        "Weight change of 5 lbs. in 5 days",
      ],
      redAlert: ["Weight change of >/= 5 lbs. in 1 day"],
    },
  ],
};
